/**
 * Adyen Web Components Style Mapper
 * 
 * Converts Altruon styleCustomization object to Adyen Web Components style format.
 * 
 * Adyen Web Components accepts styles in this structure:
 * {
 *   base: { ... },        // Default state
 *   error: { ... },       // Error state
 *   placeholder: { ... }, // Placeholder state
 *   validated: { ... }    // Valid state
 * }
 * 
 * @see https://docs.adyen.com/payment-methods/cards/custom-card-integration#styling
 */

/**
 * Maps Altruon style object to Adyen Web Components style format
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object from StyleContext
 * @returns {Object} Adyen Web Components compatible style object
 */
export const mapStylesToAdyen = (altruonStyles) => {
  if (!altruonStyles) return getDefaultAdyenStyles();

  const {
    components = {},
    colors = {},
    typography = {},
  } = altruonStyles;

  const input = components.input || {};
  const label = components.label || {};

  // Ensure we always have a visible text color (never transparent or empty)
  const textColor = input.textColor || colors.textPrimary || '#111827';
  const finalTextColor = textColor && textColor !== 'transparent' && textColor !== 'rgba(0,0,0,0)' 
    ? textColor 
    : '#111827';

  return {
    base: {
      color: finalTextColor,
      fontSize: input.fontSize || typography.fontSizes?.md || '16px',
      fontFamily: typography.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
      fontWeight: input.fontWeight || typography.fontWeights?.normal || '400',
      lineHeight: typography.lineHeight || '1.5',
      letterSpacing: typography.letterSpacing || 'normal',
      padding: input.padding || '12px 16px',
    },
    error: {
      color: colors.error || components.errorMessage?.color || '#EF4444',
    },
    placeholder: {
      color: input.placeholderColor || colors.textSecondary || '#9CA3AF',
    },
    validated: {
      color: colors.success || '#10B981',
    },
  };
};

/**
 * Returns default Adyen styles (fallback)
 * 
 * @returns {Object} Default Adyen Web Components style object
 */
const getDefaultAdyenStyles = () => ({
  base: {
    color: '#000000',  // Pure black for maximum visibility
    fontSize: '16px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
    fontWeight: '400',
    lineHeight: '1.5',
    letterSpacing: 'normal',
  },
  error: {
    color: '#EF4444',
  },
  placeholder: {
    color: '#9CA3AF',
  },
  validated: {
    color: '#10B981',
  },
});

/**
 * Maps Altruon styles to Adyen Checkout configuration
 * 
 * @param {Object} altruonStyles - The Altruon styleCustomization object
 * @returns {Object} Adyen configuration object with styling
 */
export const createAdyenConfiguration = (altruonStyles) => {
  if (!altruonStyles) return {};

  const { colors = {}, components = {} } = altruonStyles;

  return {
    // Hide pay button - we handle submission
    showPayButton: false,
    
    // Card component specific configuration
    hasHolderName: true,
    holderNameRequired: true,
    enableStoreDetails: true, // For tokenization
    
    // Apply custom styles
    styles: mapStylesToAdyen(altruonStyles),
    
    // Optional: Add placeholders
    placeholders: {
      cardNumber: '1234 5678 9012 3456',
      expiryDate: 'MM/YY',
      securityCode: '123',
      holderName: 'J. Smith',
    },
  };
};

/**
 * Maps card brand codes to display names
 * 
 * @param {string} brandCode - Adyen card brand code (e.g., 'visa', 'mc')
 * @returns {string} Display name (e.g., 'Visa', 'Mastercard')
 */
export const getCardBrandName = (brandCode) => {
  const brandNames = {
    'visa': 'Visa',
    'mc': 'Mastercard',
    'amex': 'American Express',
    'diners': 'Diners Club',
    'discover': 'Discover',
    'jcb': 'JCB',
    'maestro': 'Maestro',
    'cup': 'China UnionPay',
    'elo': 'Elo',
    'hipercard': 'Hipercard',
    'cartebancaire': 'Carte Bancaire',
  };

  return brandNames[brandCode?.toLowerCase()] || 'Card';
};

/**
 * Determines environment from gateway connection or config
 * 
 * @param {Object} gatewayConnection - Gateway connection object
 * @returns {string} 'test' or 'live'
 */
/**
 * Adyen fires NETWORK_ERROR when analytics/checkoutAttemptId fetch fails (e.g. ad-blocker).
 * Payment still works — do not show these on the card form.
 */
export const isIgnorableAdyenErrorMessage = (message) => {
  if (!message) return false;
  const normalized = String(message).trim().toLowerCase();
  return (
    normalized === 'network error' ||
    normalized === 'network_error' ||
    normalized.includes('checkoutattemptid') ||
    normalized.includes('analytics:') ||
    normalized.includes('failed to fetch')
  );
};

export const isIgnorableAdyenError = (err) => {
  if (!err) return false;
  if (err?.errorType === 'binLookup') return true;
  if (err?.name === 'NETWORK_ERROR' || err?.code === 'NETWORK_ERROR') {
    return true;
  }
  const msg = err?.message || err?.error || (typeof err === 'string' ? err : '');
  return isIgnorableAdyenErrorMessage(msg);
};

/** Map Adyen SDK / bin-lookup error payloads to checkout-friendly text. */
export const formatAdyenCardError = (err, t) => {
  if (!err || isIgnorableAdyenError(err)) return null;
  const raw =
    (typeof err === 'string' && err) ||
    err?.message ||
    err?.error ||
    err?.errorMessage ||
    null;
  if (!raw) return null;

  const code = String(raw).trim();
  if (code === 'unknownError' || err?.errorType === 'binLookup') {
    return (
      t?.('paymentForms.card.binLookupFailed') ||
      'Could not verify this card. Check the number and try again.'
    );
  }
  return code;
};

/** Client key must match Adyen environment (test_ vs live_). */
export const validateAdyenClientKey = (clientKey, environment = 'test') => {
  if (!clientKey || typeof clientKey !== 'string' || !clientKey.trim()) {
    return { valid: false, reason: 'missing' };
  }
  const key = clientKey.trim();
  if (!key.startsWith('test_') && !key.startsWith('live_')) {
    return { valid: false, reason: 'invalid_prefix' };
  }
  const env = String(environment).toLowerCase();
  if (env === 'test' && !key.startsWith('test_')) {
    return { valid: false, reason: 'test_key_required' };
  }
  if ((env === 'live' || env === 'production') && !key.startsWith('live_')) {
    return { valid: false, reason: 'live_key_required' };
  }
  return { valid: true, reason: null };
};

export const getAdyenClientKeySetupError = (reason, t) => {
  if (reason === 'config_timeout' && typeof window !== 'undefined') {
    const origin = window.location.origin;
    const template =
      t?.('paymentForms.card.encryptionNotReady') ||
      'Card encryption did not initialize. In Adyen Customer Area → Client settings → Authentication, add this exact allowed origin: {origin}';
    return template.replace('{origin}', origin);
  }

  const messages = {
    missing:
      t?.('paymentForms.card.missingClientKey') ||
      'Adyen client key is missing. Add it in Payment Gateways settings.',
    invalid_prefix:
      t?.('paymentForms.card.invalidClientKey') ||
      'Adyen client key is invalid (must start with test_ or live_).',
    test_key_required:
      t?.('paymentForms.card.testClientKeyRequired') ||
      'Use a test_ client key with the test environment.',
    live_key_required:
      t?.('paymentForms.card.liveClientKeyRequired') ||
      'Use a live_ client key with the live environment.',
  };
  return messages[reason] || messages.missing;
};

/** Client key from gateway credentials (snake_case or camelCase). */
export const getAdyenClientKey = (gatewayConnection) => {
  const creds = gatewayConnection?.credentials;
  if (!creds) return null;
  return creds.client_key || creds.clientKey || null;
};

/**
 * Normalize /paymentMethods payload for Adyen Web v6 (scheme + full nested fields).
 * @see https://docs.adyen.com/payment-methods/cards/web-component
 */
export const normalizeAdyenPaymentMethodsResponse = (data) => {
  if (!data || !Array.isArray(data.paymentMethods)) {
    return null;
  }

  const paymentMethods = data.paymentMethods.map((pm) => {
    if (pm?.type === 'card') {
      return { ...pm, type: 'scheme' };
    }
    return pm;
  });

  const scheme = paymentMethods.find((pm) => pm?.type === 'scheme');
  if (!scheme) {
    return null;
  }

  return {
    ...data,
    paymentMethods,
    storedPaymentMethods: data.storedPaymentMethods || [],
  };
};

/**
 * Isolated Card component must only see scheme. A paid /paymentMethods call
 * also returns ACH, Click to Pay config, etc., and Adyen Web then mounts a
 * type switcher (bank + card icons) with no card fields.
 */
export const cardOnlyAdyenPaymentMethods = (data) => {
  const normalized = normalizeAdyenPaymentMethodsResponse(data);
  if (!normalized) return null;
  return {
    ...normalized,
    paymentMethods: normalized.paymentMethods.filter((pm) => pm?.type === "scheme"),
    storedPaymentMethods: [],
    configuration: undefined,
  };
};

export const isAdyenPaymentMethodsReady = (paymentMethodsResponse) => {
  const normalized = normalizeAdyenPaymentMethodsResponse(paymentMethodsResponse);
  if (!normalized) return false;
  const scheme = normalized.paymentMethods.find((pm) => pm.type === 'scheme');
  return Boolean(scheme);
};

/** Pull encrypted card fields from Adyen onChange/submit state. */
export const extractAdyenEncryptedCardData = (state) => {
  if (!state?.data) return null;

  const pm = state.data.paymentMethod || state.data;
  if (!pm?.encryptedCardNumber) return null;

  return {
    encryptedCardNumber: pm.encryptedCardNumber,
    encryptedExpiryMonth: pm.encryptedExpiryMonth,
    encryptedExpiryYear: pm.encryptedExpiryYear,
    encryptedSecurityCode: pm.encryptedSecurityCode,
    holderName: pm.holderName ?? state.data.holderName,
    browserInfo: state.data.browserInfo || null,
  };
};

export const isAdyenCardDataComplete = (cardData) => {
  if (!cardData) return false;
  return Boolean(
    cardData.encryptedCardNumber &&
      cardData.encryptedExpiryMonth &&
      cardData.encryptedExpiryYear &&
      cardData.encryptedSecurityCode &&
      cardData.holderName
  );
};

export const getAdyenEnvironment = (gatewayConnection) => {
  const credentialEnv = gatewayConnection?.credentials?.environment;
  if (credentialEnv) {
    const normalized = String(credentialEnv).trim().toLowerCase();
    if (normalized === 'live' || normalized === 'production') {
      return 'live';
    }
    return 'test';
  }

  if (gatewayConnection?.sandbox || gatewayConnection?.test_mode) {
    return 'test';
  }

  if (gatewayConnection?.environment) {
    const normalized = String(gatewayConnection.environment).trim().toLowerCase();
    return normalized === 'live' || normalized === 'production' ? 'live' : 'test';
  }

  return 'test';
};

/**
 * Validates Adyen configuration before initialization
 * 
 * @param {Object} config - Adyen configuration
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateAdyenConfig = (config) => {
  const errors = [];

  if (!config.clientKey) {
    errors.push('clientKey is required');
  }

  if (!config.environment) {
    errors.push('environment is required (test or live)');
  }

  if (!config.paymentMethodsResponse) {
    errors.push('paymentMethodsResponse is required');
  }

  if (!config.locale) {
    errors.push('locale is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Gets locale from currency code for Adyen initialization
 * 
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Locale code (e.g., 'en-US', 'pt-BR')
 */
export const getLocaleFromCurrency = (currencyCode) => {
  const currencyToLocale = {
    'USD': 'en-US',
    'EUR': 'en-US', // Changed from en-GB to en-US (widely supported)
    'GBP': 'en-US', // Changed from en-GB to en-US (widely supported)
    'BRL': 'pt-BR',
    'MXN': 'es-ES', // Changed from es-MX to es-ES (widely supported)
    'ARS': 'es-ES', // Changed from es-AR to es-ES (widely supported)
    'CLP': 'es-ES', // Changed from es-CL to es-ES (widely supported)
    'COP': 'es-ES', // Changed from es-CO to es-ES (widely supported)
    'PEN': 'es-ES', // Changed from es-PE to es-ES (widely supported)
    'CAD': 'en-US', // Changed from en-CA to en-US (widely supported)
    'AUD': 'en-US', // Changed from en-AU to en-US (widely supported)
    'JPY': 'ja-JP',
    'CNY': 'zh-CN',
    'KRW': 'ko-KR',
    'INR': 'en-US', // Changed from en-IN to en-US (widely supported)
    'SGD': 'en-US', // Changed from en-SG to en-US (widely supported)
    'HKD': 'zh-CN', // Changed from zh-HK to zh-CN (widely supported)
    'THB': 'en-US', // Changed from th-TH to en-US (widely supported)
    'MYR': 'en-US', // Changed from en-MY to en-US (widely supported)
    'IDR': 'en-US', // Changed from id-ID to en-US (widely supported)
    'PHP': 'en-US', // Changed from en-PH to en-US (widely supported)
    'VND': 'en-US', // Changed from vi-VN to en-US (widely supported)
    'ZAR': 'en-US', // Changed from en-ZA to en-US (widely supported)
    'TRY': 'en-US', // Changed from tr-TR to en-US (widely supported)
    'SAR': 'en-US', // Changed from ar-SA to en-US (widely supported)
    'AED': 'en-US', // Changed from ar-AE to en-US (widely supported)
  };

  return currencyToLocale[currencyCode?.toUpperCase()] || 'en-US';
};

/**
 * Gets country code from currency code for Adyen initialization
 * Required by Adyen when initializing checkout
 * 
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} ISO 3166-1 alpha-2 country code (e.g., 'US', 'IN')
 */
export const getCountryFromCurrency = (currencyCode) => {
  const currencyToCountry = {
    'INR': 'IN', // India
    'USD': 'US', // United States
    'EUR': 'FR', // France (default for EUR)
    'GBP': 'GB', // United Kingdom
    'BRL': 'BR', // Brazil
    'MXN': 'MX', // Mexico
    'ARS': 'AR', // Argentina
    'CLP': 'CL', // Chile
    'COP': 'CO', // Colombia
    'PEN': 'PE', // Peru
    'UYU': 'UY', // Uruguay
    'PYG': 'PY', // Paraguay
    'BOB': 'BO', // Bolivia
    'CAD': 'CA', // Canada
    'AUD': 'AU', // Australia
    'JPY': 'JP', // Japan
    'CNY': 'CN', // China
    'KRW': 'KR', // South Korea
    'SGD': 'SG', // Singapore
    'HKD': 'HK', // Hong Kong
    'THB': 'TH', // Thailand
    'MYR': 'MY', // Malaysia
    'IDR': 'ID', // Indonesia
    'PHP': 'PH', // Philippines
    'VND': 'VN', // Vietnam
    'PKR': 'PK', // Pakistan
    'BDT': 'BD', // Bangladesh
    'EGP': 'EG', // Egypt
    'NGN': 'NG', // Nigeria
    'KES': 'KE', // Kenya
    'GHS': 'GH', // Ghana
    'ZAR': 'ZA', // South Africa
    'MAD': 'MA', // Morocco
    'TRY': 'TR', // Turkey
    'SAR': 'SA', // Saudi Arabia
    'AED': 'AE', // United Arab Emirates
    'QAR': 'QA', // Qatar
    'OMR': 'OM', // Oman
    'BHD': 'BH', // Bahrain
    'JOD': 'JO', // Jordan
  };

  return currencyToCountry[currencyCode?.toUpperCase()] || 'US';
};

export default {
  mapStylesToAdyen,
  createAdyenConfiguration,
  getCardBrandName,
  getAdyenEnvironment,
  validateAdyenConfig,
  getLocaleFromCurrency,
  getCountryFromCurrency,
};

